import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import StatCard from './StatCard';
import ClientSelector from './ClientSelector';
import { 
  FileText, 
  Clock, 
  Search, 
  CheckCircle, 
  Calendar, 
  TrendingUp,
  Building2,
  Users,
  AlertTriangle,
  Target,
  Filter
} from 'lucide-react';
// import { toast } from 'react-toastify';

const MasterDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtro por cliente
  const [selectedClient, setSelectedClient] = useState('all');
  
  // Se for client-admin, não carregar dados do master dashboard
  const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [companyError, setCompanyError] = useState(null);

  const loadDashboardData = async (clientSlug = selectedClient) => {
    try {
      setLoading(true);
      
      // Se for client-admin, não carregar dados do master dashboard
      if (isClientAdmin) {
        console.log('🏢 Client-admin detectado, não carregando dados do master dashboard');
        setError('Client-admin não tem acesso ao dashboard master. Use a aba "Dashboard do Cliente".');
        setLoading(false);
        return;
      }
      
      console.log('🔄 Carregando dados do dashboard master...');
      console.log('🔍 Filtro por cliente:', clientSlug);
      
      // Construir URL com filtro de cliente
      const url = clientSlug && clientSlug !== 'all' 
        ? `/api/complaints/master/dashboard?client_slug=${clientSlug}`
        : '/api/complaints/master/dashboard';
      
      const response = await axios.get(url);
      console.log('📊 Resposta recebida:', response.data);
      
      if (response.data.success) {
        console.log('✅ Dados carregados com sucesso:', response.data.data);
        setDashboardData(response.data.data);
      } else {
        console.error('❌ Erro na resposta:', response.data);
        setError('Erro ao carregar dados do dashboard');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dashboard:', error);
      console.error('❌ Detalhes do erro:', error.response?.data);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDetails = async (clientSlug) => {
    try {
      console.log('🔍 Carregando detalhes da empresa:', clientSlug);
      setLoadingCompany(true);
      setCompanyError(null);
      const response = await axios.get(`/api/complaints/master/company/${clientSlug}`);
      
      console.log('✅ Resposta recebida:', response.data);
      
      if (response.data.success) {
        console.log('✅ Empresa selecionada:', response.data.data);
        setSelectedCompany(response.data.data);
        console.log('✅ Estado selectedCompany atualizado para:', response.data.data);
      } else {
        setCompanyError('Erro ao carregar detalhes da empresa');
        console.log('❌ Erro na resposta:', response.data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar detalhes da empresa:', error);
      setCompanyError('Erro ao carregar detalhes da empresa');
    } finally {
      setLoadingCompany(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [selectedClient]); // Recarregar quando o cliente selecionado mudar

  const getStatusColor = (status) => {
    const colors = {
      'received': 'bg-yellow-100 text-yellow-800',
      'investigating': 'bg-blue-100 text-blue-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'received': 'Recebidas',
      'investigating': 'Em Investigação',
      'resolved': 'Resolvidas',
      'closed': 'Fechadas'
    };
    return labels[status] || status;
  };


  const CompanyCard = ({ company }) => {
    const handleClick = () => {
      console.log('🖱️ Botão Ver Detalhes clicado para:', company.client_name, company.client_slug);
      loadCompanyDetails(company.client_slug);
    };

    return (
      <div 
        className="rf-surface p-6 hover:shadow-xl transition-all duration-300 cursor-pointer border-l-4 border-blue-500"
        onClick={handleClick}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{company.client_name}</h3>
          <span className="text-sm text-gray-500">{company.client_slug}</span>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Total de Denúncias: {company.total_complaints}</p>
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center">
            <p className="font-semibold text-green-600">{company.received_complaints}</p>
            <p className="text-gray-500">Recebidas</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-yellow-600">{company.investigating_complaints}</p>
            <p className="text-gray-500">Investigando</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-blue-600">{company.resolved_complaints}</p>
            <p className="text-gray-500">Resolvidas</p>
          </div>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
        >
          Ver Detalhes
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadDashboardData}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (selectedCompany) {
    console.log('🎯 Renderizando dashboard detalhado para:', selectedCompany.client_name);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedCompany(null)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            ← Voltar ao Dashboard
          </button>
          <h2 className="text-2xl font-bold text-gray-900">
            📊 Dashboard Detalhado - {selectedCompany.client_name}
          </h2>
        </div>

        {loadingCompany ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Carregando detalhes...</p>
          </div>
        ) : companyError ? (
          <div className="text-center py-8">
            <p className="text-red-600">{companyError}</p>
            <button
              onClick={() => loadCompanyDetails(selectedCompany.client_slug)}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
            >
              Tentar Novamente
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Gerais da Empresa */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <StatCard
                title="Total de Denúncias"
                value={selectedCompany.total_complaints}
                icon="📝"
                color="text-blue-600"
              />
              <StatCard
                title="Recebidas"
                value={selectedCompany.received_complaints}
                icon="⏳"
                color="text-yellow-600"
              />
              <StatCard
                title="Em Investigação"
                value={selectedCompany.investigating_complaints}
                icon="🔍"
                color="text-purple-600"
              />
              <StatCard
                title="Resolvidas"
                value={selectedCompany.resolved_complaints}
                icon="✅"
                color="text-green-600"
              />
            </div>

            {/* Denúncias por Unidade */}
            {selectedCompany.unit_stats && selectedCompany.unit_stats.length > 0 && (
              <div className="rf-surface p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">🏢 Denúncias por Unidade</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedCompany.unit_stats.map((unit, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{unit.unit_name}</h4>
                      <div className="space-y-1 text-sm">
                        <p>Total: {unit.total_complaints}</p>
                        <p>Recebidas: {unit.received_complaints}</p>
                        <p>Investigando: {unit.investigating_complaints}</p>
                        <p>Resolvidas: {unit.resolved_complaints}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Denúncias por Tipo */}
            {selectedCompany.type_stats && selectedCompany.type_stats.length > 0 && (
              <div className="rf-surface p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Denúncias por Tipo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedCompany.type_stats.map((type, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{type.complaint_type}</h4>
                      <div className="space-y-1 text-sm">
                        <p>Total: {type.total_complaints}</p>
                        <p>Recebidas: {type.received_complaints}</p>
                        <p>Investigando: {type.investigating_complaints}</p>
                        <p>Resolvidas: {type.resolved_complaints}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🏢 Dashboard Master</h1>
          <p className="text-gray-600">Visão geral de todas as empresas</p>
        </div>
        
        {/* Filtro de Cliente */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtrar por cliente:</span>
          </div>
          <ClientSelector
            selectedClient={selectedClient}
            onClientChange={(client) => setSelectedClient(client.client_slug)}
            className="w-64"
          />
        </div>
      </div>

      {/* Stats Gerais - Layout Melhorado */}
      <div 
        className="gap-4" 
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem'
        }}
      >
        <StatCard
          title="Total de Denúncias"
          value={dashboardData?.general_stats?.total_complaints}
          icon={FileText}
          color="fius"
          subtitle="Todas as denúncias"
        />
        <StatCard
          title="Recebidas"
          value={dashboardData?.general_stats?.received_complaints}
          icon={Clock}
          color="yellow"
          subtitle="Aguardando análise"
        />
        <StatCard
          title="Em Investigação"
          value={dashboardData?.general_stats?.investigating_complaints}
          icon={Search}
          color="purple"
          subtitle="Em andamento"
        />
        <StatCard
          title="Resolvidas"
          value={dashboardData?.general_stats?.resolved_complaints || 0}
          icon={CheckCircle}
          color="green"
          subtitle="Concluídas"
        />
        <StatCard
          title="Últimos 30 dias"
          value={dashboardData?.general_stats?.last_30_days}
          icon={Calendar}
          color="indigo"
          subtitle="Período recente"
        />
        <StatCard
          title="Taxa Resolução"
          value={`${Math.round(((dashboardData?.general_stats?.resolved_complaints || 0) / (dashboardData?.general_stats?.total_complaints || 1)) * 100)}%`}
          icon={TrendingUp}
          color="emerald"
          subtitle="Eficiência"
        />
      </div>

      {/* Empresas */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">🏢 Empresas Cadastradas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(Array.isArray(dashboardData?.company_stats) ? dashboardData.company_stats : []).map((company, index) => (
            <CompanyCard
              key={company.client_slug}
              company={company}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MasterDashboard;